import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { EmbedSnippet } from "../components/EmbedSnippet";
import { agentKeys } from "../queryKeys";

const DOMAIN_REGEX = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

function normalizeDomain(value: string): string {
  if (!value || typeof value !== "string") return "";
  let s = value.trim().toLowerCase();
  if (s.includes("://")) s = s.split("://", 1)[1] ?? s;
  s = s.split("/")[0].split("?")[0].trim();
  return s;
}

function isValidDomain(domain: string): boolean {
  if (!domain) return true;
  return DOMAIN_REGEX.test(domain);
}

interface AgentData {
  id: string;
  domain: string;
}

interface IntegrationsPageProps {
  agentId: string;
  onSave?: () => Promise<void>;
}

export function IntegrationsPage({ agentId, onSave }: IntegrationsPageProps) {
  const queryClient = useQueryClient();
  const { data: serverAgent, isLoading: loading } = useQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => apiFetch<{ agent: AgentData }>(`/api/agents/${agentId}`).then((r) => r.agent),
  });
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setAgent(serverAgent ?? null);
  }, [serverAgent]);

  const updateField = useCallback((field: keyof AgentData, value: string) => {
    if (!agent) return;
    setAgent({ ...agent, [field]: value });
  }, [agent]);

  const saveMutation = useMutation({
    mutationFn: (payload: AgentData) =>
      apiFetch<{ agent: AgentData }>(`/api/agents/${agentId}`, {
        method: "PUT",
        body: JSON.stringify({ domain: payload.domain }),
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      if (onSave) await onSave();
    },
    onError: (err: Error) => setError(err.message || "Save failed"),
  });

  const handleSave = useCallback(async () => {
    if (!agent) return;
    setError("");
    saveMutation.mutate(agent);
  }, [agent, saveMutation]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).__integrationsSaveHandler = handleSave;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete (window as any).__integrationsSaveHandler;
      }
    };
  }, [handleSave]);

  if (loading)
    return (
      <div className="integrations-settings">
        <p className="dash-empty">Loading...</p>
      </div>
    );
  if (!agent && !loading)
    return (
      <div className="integrations-settings">
        <p className="dash-empty">{error || "Agent not found"}</p>
      </div>
    );

  return (
    <div className="integrations-settings">
      <div className="settings-section">
        <h4 className="section-header">
          <span className="material-symbols-outlined">language</span>
          Domain Configuration
        </h4>
        <div className="form-grid">
          <label>
            Website Domain
            <input
              type="text"
              value={agent.domain || ""}
              onChange={(e) => updateField("domain", e.target.value)}
              onBlur={(e) => {
                const cleaned = normalizeDomain(e.target.value);
                if (cleaned !== (agent.domain || "")) updateField("domain", cleaned);
              }}
              placeholder="e.g. example.com or www.example.com"
            />
            <div className="form-tip">
              Restrict widget access to a specific domain (optional). Use a clean hostname like example.com or www.example.com (no protocol or path).
            </div>
            {agent.domain && !isValidDomain(agent.domain) && (
              <p className="form-tip" style={{ color: "var(--danger)", marginTop: "0.25rem" }}>
                Invalid domain. Use only hostname, e.g. example.com or www.example.com
              </p>
            )}
          </label>
        </div>
      </div>

      <div className="settings-section">
        <h4 className="section-header">
          <span className="material-symbols-outlined">code</span>
          Embed Code
        </h4>
        <EmbedSnippet agentId={agent.id} />
      </div>

      <div className="settings-section">
        <h4 className="section-header">
          <span className="material-symbols-outlined">webhook</span>
          Webhooks & Integrations
        </h4>
        <div className="integrations-placeholder">
          <p>Webhook URLs, Slack, Discord, and other integrations coming soon.</p>
        </div>
      </div>

      {(error || saveMutation.error) && (
        <div className="auth-error">{error || (saveMutation.error as Error)?.message}</div>
      )}
    </div>
  );
}
